// loader ↔ embed 通信协议 & 对外类型定义
//
// 设计对齐 palm-mobile/Android/PalmMobileManager：
//   - Mode / Params / Result / *Data / PalmDirection / ResultCode 与 Java 侧一一对应
//   - 枚举值采用字符串，方便 postMessage 透传与调试
//
// 跨 iframe 通信统一用 Envelope 包裹，避免与宿主页其它 iframe 冲突。

export const PROTOCOL_NAMESPACE = 'palm-mobile-manager' as const;
export const PROTOCOL_VERSION = 1 as const;

/** 业务模式 —— 对齐 Android `PalmMobileManager.Mode` */
export enum Mode {
  Registration = 'registration',
  Verification = 'verification',
  Recognition = 'recognition',
}

/** 手掌方向 —— 对齐 Android `PalmDirection`，序列化值取其 @SerializedName */
export enum PalmDirection {
  Unspecified = 'palm_direction_unspecified',
  Left = 'left',
  Right = 'right',
}

/**
 * 结果码 —— 与 Android `com.tencent.palm.core.Result` 保持同值同义，
 * 便于同一套后端/业务日志体系复用。
 */
export enum ResultCode {
  Success = 0,
  ErrUnknown = 10000,
  ErrInvalidParams = 10001,
  ErrUserCancelled = 10002,
  ErrCameraPermissionDenied = 10003,
  ErrCameraInitFailed = 10004,
  ErrCameraPreviewUnsupported = 10005,
  ErrSdkInitFailed = 10006,
  ErrSdkRuntime = 10007,
  ErrAcquisitionTimeout = 10008,
  ErrUserTokenInvalid = 10012,
  ErrLicenseValidationFailed = 10016,
  ErrUserNameInvalid = 10017,
  ErrUserIdInvalid = 10018,
  ErrPhoneInvalid = 10019,
  ErrAppDisabled = 10020,
  ErrUserNotExist = 10021,
  /** create_mobile_user code=36：租户后台关闭了 APP 端注册入口 */
  ErrTenantRegistrationDisabled = 10022,
  /** create_mobile_user code=36：手机号已被同租户其它用户占用 */
  ErrTenantPhoneAlreadyExists = 10023,
  /** register_mobile_palm 1001030：租户后台关闭了 APP 端手掌注册 */
  ErrTenantPalmRegistrationDisabled = 10024,

  // Registration
  ErrLivenessFailed = 10100,
  ErrQualityFailed = 10101,
  ErrLivenessVideoFailed = 10102,
  ErrAlreadyBind = 10103,
  ErrHighSimilarity = 10104,
  /**
   * 客户端本地活体视频录制失败 —— web 端专有：
   *   * MediaRecorder 不可用（浏览器不支持 / 未在 secure context）
   *   * 采集途中 recorder onerror / track 被抢占，导致最终拿不到 blob
   * 与 10102 (ErrLivenessVideoFailed，后端 1001006 活体视频校验未通过) 严格区分：
   * 一个是"视频没拍到"（客户端），一个是"视频拍到了但后端判定不合格"（服务端）。
   */
  ErrLivenessVideoCaptureFailed = 10105,

  // Recognition
  ErrUserNotRecognized = 10200,

  // Verification
  ErrTargetUserNotFound = 10300,
  ErrTargetUserUnregistered = 10301,
  ErrTargetUserPalmDirectionUnregistered = 10302,

  // Network
  ErrUnauthorized = 10401,
  ErrNetwork = 10500,

  // Verification record report (SDK 内部使用，通常不回调宿主页)
  /** 核验业务失败兜底 —— Verification 未匹配且无更具体错误码，用于上报后台 */
  ErrCompareFailed = 50002,
  /** 识别业务失败兜底 —— Recognition 未拿到 userId 且无更具体错误码，用于上报后台 */
  ErrRecognitionFailed = 50003,
  /** 命中活体黑名单 —— 掌纹已被后台标记为拒绝准入 */
  ErrBlacklistHit = 50005,
}

/**
 * 日志等级 —— 数值越大越重要。
 * 数值约定参考 syslog，但简化为 5 档。
 */
export enum LogLevel {
  Debug = 10,
  Info = 20,
  Warn = 30,
  Error = 40,
  Silent = 100,
}

export type LogLevelName = 'debug' | 'info' | 'warn' | 'error' | 'silent';

/** 启动参数 —— 对齐 Android `PalmMobileManager.Params` */
export interface Params {
  token: string;
  userId: string;
  userName: string;
  phoneNo: string;
  mode: Mode;

  /** Mode=Verification 时必填 */
  targetUserId?: string;

  /**
   * 对齐 Android `Params.Builder.setPalmDirection`。
   * 不传 / Unspecified = 任意手（算法自适配），等价 native `UNSPECIFIED`。
   */
  palmDirection?: PalmDirection;

  /** 默认 {@link DEFAULT_APP_ID} */
  appId?: number;
  /** 默认 true */
  enableManager?: boolean;
  customHeaders?: Record<string, string>;

  /**
   * 界面语言（BCP-47，如 'zh-Hans' / 'zh-Hant' / 'en' / 'ja' / 'id' / 'fr'）。
   *
   * 宿主 UI 语言与系统语言不一致时必须显式传入（例如中文系统里切到英文界面），
   * 否则 SDK 只能按 navigator.language 推断。传入 SDK 未支持的语言时回退英文，
   * 不传则跟随系统语言。
   */
  locale?: string;
}

export const DEFAULT_APP_ID = 223;

/** 校验 Params，返回 null 代表通过，否则返回人类可读的错误原因 */
export function validateParams(p: Params): string | null {
  if (!p.token?.trim()) return 'token cannot be empty.';
  if (!p.userId?.trim()) return 'userId cannot be empty.';
  if (!p.userName?.trim()) return 'userName cannot be empty.';
  if (!p.phoneNo?.trim()) return 'phoneNo cannot be empty.';
  if (p.mode === Mode.Verification && !p.targetUserId?.trim()) {
    return 'targetUserId cannot be empty when mode is VERIFICATION.';
  }
  const appId = p.appId ?? DEFAULT_APP_ID;
  if (!Number.isInteger(appId) || appId <= 0) {
    return 'appId must be a positive integer.';
  }
  return null;
}

// ---------- 结果数据 ----------

// 后端 wire 上 palmDirection 是 int (0/1/2)，这里统一收敛成 PalmDirection 字符串枚举，
// 转换发生在 mapResponseToResultData。
export interface RegistrationData {
  userId: string;
  palmDirection?: PalmDirection;
}

export interface RecognitionData {
  userId: string;
  score: number;
  userName?: string;
  palmDirection?: PalmDirection;
}

export interface VerificationData {
  isMatch: boolean;
  score: number;
  palmDirection?: PalmDirection;
}

/**
 * 手掌方向枚举 ↔ 后端 int wire（0=unspecified / 1=left / 2=right）。
 * 单一事实来源：两个方向转换都从这张表派生，避免各处分支/字面量重复。
 */
const PALM_DIRECTION_CODES: ReadonlyArray<{ dir: PalmDirection; code: number }> = [
  { dir: PalmDirection.Unspecified, code: 0 },
  { dir: PalmDirection.Left, code: 1 },
  { dir: PalmDirection.Right, code: 2 },
];

/** 后端 int wire → 枚举 */
export function palmDirectionFromCode(code: number | undefined): PalmDirection {
  return PALM_DIRECTION_CODES.find((x) => x.code === code)?.dir ?? PalmDirection.Unspecified;
}

/** 枚举 → 后端 int wire（查黑名单等比对用） */
export function palmDirectionToCode(d: PalmDirection | undefined): number {
  return PALM_DIRECTION_CODES.find((x) => x.dir === (d ?? PalmDirection.Unspecified))?.code ?? 0;
}

export type ResultData = RegistrationData | RecognitionData | VerificationData;

/** 统一结果 —— 对齐 Android `PalmMobileManager.Result` */
export interface Result {
  code: ResultCode;
  message: string;
  data?: ResultData;
  /** 失败时的后端调用 traceid（请求头 X-Traceid），供截屏后定位后端日志 */
  traceId?: string;
}

// ---------- loader ↔ embed 内部消息 ----------

export type LoaderToEmbed =
  | {
      type: 'start';
      payload: Params;
      /** loader 侧生成；embed 用作 logger sessionId，把两侧日志串起来 */
      sessionId: string;
    }
  /** 宿主/native 转发系统返回键或返回手势 */
  | { type: 'back' };

/**
 * embed → loader：
 *   - ready：embed 页面加载完毕并准备好接收 start
 *   - result：采集结束，返回最终 Result
 */
export type EmbedToLoader =
  | { type: 'ready'; version: number }
  | { type: 'result'; payload: Result };

export interface Envelope<T> {
  ns: typeof PROTOCOL_NAMESPACE;
  v: typeof PROTOCOL_VERSION;
  msg: T;
}

export const wrap = <T>(msg: T): Envelope<T> => ({
  ns: PROTOCOL_NAMESPACE,
  v: PROTOCOL_VERSION,
  msg,
});

export const isEnvelope = <T>(raw: unknown): raw is Envelope<T> => {
  if (!raw || typeof raw !== 'object') return false;
  const e = raw as Partial<Envelope<T>>;
  return e.ns === PROTOCOL_NAMESPACE && e.v === PROTOCOL_VERSION && e.msg !== undefined;
};

export type Callback = (result: Result) => void;
